import type {
  DocumentOrigin,
  PrincipalOptions,
  ProjectDocument,
  ProjectMemberView,
  ProjectView,
  Reader,
} from "@esi/shared";
import { type ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";
import { NativeSelect } from "@/components/NativeSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import type { KnowledgeSettings } from "./queries.ts";
import {
  type Audience,
  audienceAllowed,
  audienceReaders,
  projectAudiences,
  shortId,
} from "./readers.ts";

/** Who may read a file, its classification and origin, as the upload and edit forms hold them. */
export interface AccessState {
  classification: string;
  audience: Audience;
  named: ReadonlySet<string>;
  groups: ReadonlySet<string>;
  rules: ReadonlySet<string>;
  origin: DocumentOrigin;
}

const hasPrincipals = (principals: PrincipalOptions | undefined) =>
  (principals?.groups.length ?? 0) + (principals?.rules.length ?? 0) > 0;

/** The audiences offered for this project (groups only when the tenant has some). */
export function offeredAudiences(
  project: Pick<ProjectView, "owner">,
  principals: PrincipalOptions | undefined,
): Audience[] {
  return projectAudiences(project, { groups: hasPrincipals(principals) });
}

/** A new upload: the default classification and the widest audience it allows. */
export function initialAccess(
  project: Pick<ProjectView, "owner">,
  settings: KnowledgeSettings,
  principals: PrincipalOptions | undefined,
): AccessState {
  const level = settings.scheme.find((l) => l.id === settings.defaultClassification);
  return {
    classification: settings.defaultClassification,
    audience:
      offeredAudiences(project, principals).find((a) => audienceAllowed(a, level)) ?? "named",
    named: new Set(),
    groups: new Set(),
    rules: new Set(),
    origin: "internal",
  };
}

/**
 * An existing file's access in the form's terms. Reader sets the form cannot express (mixed
 * kinds set through the API) fall back to the named people among them; their readers are only
 * sent if the person editing changes them.
 */
export function accessOf(
  document: Pick<ProjectDocument, "readers" | "classification" | "origin" | "uploadedBy">,
): AccessState {
  const kinds = new Set(document.readers.map((r) => r.kind));
  const refs = (kind: string) =>
    new Set(document.readers.filter((r) => r.kind === kind).map((r) => r.ref.toLowerCase()));
  const single = document.readers.length === 1 ? document.readers[0] : undefined;
  const audience: Audience =
    single?.kind === "project" || single?.kind === "entity" || single?.kind === "entity_tree"
      ? single.kind
      : kinds.has("group") || kinds.has("attribute_rule")
        ? "groups"
        : "named";
  const users = refs("user");
  users.delete(document.uploadedBy.toLowerCase());
  return {
    classification: document.classification,
    audience,
    named: users,
    groups: refs("group"),
    rules: refs("attribute_rule"),
    // An origin this form cannot set (`generated`) is edited as internal.
    origin: document.origin === "external" ? "external" : "internal",
  };
}

/** The readers to send for `state`; `fixedReader` (the uploader) is always among named ones. */
export function accessReaders(
  state: AccessState,
  project: Pick<ProjectView, "id" | "owner">,
  fixedReader: string,
): Reader[] {
  return audienceReaders(state.audience, project, [...state.named], fixedReader, {
    groups: [...state.groups],
    rules: [...state.rules],
  });
}

/** True when the `groups` audience is chosen with nothing picked. */
export const nothingPicked = (state: AccessState) =>
  state.audience === "groups" && state.groups.size + state.rules.size === 0;

/**
 * Classification, readers and origin with the classification's floor rules: a level that
 * forbids the chosen audience narrows it (never widens). Shared by upload and edit access.
 */
export function AccessFields(props: {
  project: ProjectView;
  settings: KnowledgeSettings;
  /** Undefined while loading or when the list could not be loaded. */
  members: readonly ProjectMemberView[] | undefined;
  principals?: PrincipalOptions | undefined;
  value: AccessState;
  onChange: (next: AccessState) => void;
  disabled?: boolean;
  /** The person always among named readers (the uploader), and how to label them. */
  fixedReader: { id: string; label: string };
  /**
   * A derived origin (an added artefact, a saved answer, a generated file): shown, never
   * editable, since the server sets it from the sources (artefacts-to-knowledge).
   */
  fixedOrigin?: ReactNode;
}) {
  const { project, settings, members, principals, value, onChange, disabled } = props;
  const id = useId();
  const { t } = useTranslation();
  const level = settings.scheme.find((l) => l.id === value.classification);
  const audiences = offeredAudiences(project, principals);
  const others = (members ?? []).filter(
    (m) => m.userId.toLowerCase() !== props.fixedReader.id.toLowerCase(),
  );
  const set = (patch: Partial<AccessState>) => onChange({ ...value, ...patch });

  function chooseClassification(next: string) {
    const nextLevel = settings.scheme.find((l) => l.id === next);
    // Narrow, never widen, when the new level forbids the current audience.
    set({
      classification: next,
      audience: audienceAllowed(value.audience, nextLevel)
        ? value.audience
        : (audiences.find((a) => audienceAllowed(a, nextLevel)) ?? "named"),
    });
  }

  const levelHint = level
    ? [
        level.readersFloor === "named"
          ? t("upload.levelNamed")
          : level.readersFloor === "entity"
            ? t("upload.levelEntity")
            : null,
        level.downloadAllowed ? null : t("upload.levelNoDownload"),
      ]
        .filter(Boolean)
        .join(" ") || t("upload.levelAny")
    : null;

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-classification`}>{t("upload.classification")}</Label>
        <NativeSelect
          id={`${id}-classification`}
          value={value.classification}
          disabled={disabled}
          aria-describedby={`${id}-classification-hint`}
          className="sm:w-72"
          onChange={(e) => chooseClassification(e.currentTarget.value)}
        >
          {settings.scheme.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </NativeSelect>
        <p id={`${id}-classification-hint`} className="text-xs text-fg-muted">
          {levelHint}
        </p>
      </div>

      <fieldset className="flex flex-col gap-2" disabled={disabled}>
        <legend className="mb-2 text-sm font-medium text-fg">{t("upload.readers")}</legend>
        <RadioGroup
          value={value.audience}
          onValueChange={(v) => set({ audience: v as Audience })}
          disabled={disabled}
          className="gap-2.5"
        >
          {audiences.map((a) => {
            const allowed = audienceAllowed(a, level);
            return (
              <div key={a} className="flex flex-col gap-2">
                <div className="flex items-start gap-2.5">
                  <RadioGroupItem
                    id={`${id}-audience-${a}`}
                    value={a}
                    disabled={!allowed || disabled}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={`${id}-audience-${a}`}
                    className={cn("flex-col items-start gap-0.5", !allowed && "text-fg-muted")}
                  >
                    {t(`upload.audience.${a}`)}
                    {allowed || !level ? null : (
                      <span className="font-normal text-fg-muted">
                        {t("upload.audienceNotAllowed", { level: level.label })}
                      </span>
                    )}
                  </Label>
                </div>
                {a === "named" && value.audience === "named" ? (
                  <ul className="ml-6.5 flex flex-col gap-2 border-l border-line pl-3">
                    <li className="flex items-center gap-2.5">
                      <Checkbox id={`${id}-me`} checked disabled />
                      <Label htmlFor={`${id}-me`} className="font-normal text-fg-secondary">
                        {props.fixedReader.label}
                      </Label>
                    </li>
                    {others.map((m) => (
                      <li key={m.userId} className="flex items-center gap-2.5">
                        <Checkbox
                          id={`${id}-member-${m.userId}`}
                          checked={value.named.has(m.userId.toLowerCase())}
                          onCheckedChange={(on) =>
                            set({ named: toggle(value.named, m.userId.toLowerCase(), on === true) })
                          }
                        />
                        <Label htmlFor={`${id}-member-${m.userId}`} className="font-normal">
                          {m.displayName ?? shortId(m.userId)}
                          <span className="text-fg-muted">{t(`projectRoles.${m.role}.name`)}</span>
                        </Label>
                      </li>
                    ))}
                    {members === undefined ? (
                      <li className="text-sm text-fg-muted">{t("upload.membersUnavailable")}</li>
                    ) : others.length === 0 ? (
                      <li className="text-sm text-fg-muted">{t("upload.noOtherMembers")}</li>
                    ) : null}
                  </ul>
                ) : null}
                {a === "groups" && value.audience === "groups" && principals ? (
                  <PrincipalPicker
                    principals={principals}
                    groups={value.groups}
                    rules={value.rules}
                    onGroups={(groups) => set({ groups })}
                    onRules={(rules) => set({ rules })}
                    missing={nothingPicked(value)}
                  />
                ) : null}
              </div>
            );
          })}
        </RadioGroup>
      </fieldset>

      {props.fixedOrigin ? (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-fg">{t("upload.origin")}</p>
          <p className="text-sm text-fg">{props.fixedOrigin}</p>
          <p className="text-sm text-fg-secondary">{t("files.originDerived")}</p>
        </div>
      ) : (
        <fieldset className="flex flex-col gap-2" disabled={disabled}>
          <legend className="mb-2 text-sm font-medium text-fg">{t("upload.origin")}</legend>
          <RadioGroup
            value={value.origin}
            onValueChange={(v) => set({ origin: v as DocumentOrigin })}
            disabled={disabled}
            className="gap-2.5"
          >
            {(["internal", "external"] as const).map((o) => (
              <div key={o} className="flex items-start gap-2.5">
                <RadioGroupItem
                  id={`${id}-origin-${o}`}
                  value={o}
                  aria-describedby={`${id}-origin-${o}-hint`}
                  className="mt-0.5"
                />
                <div className="flex flex-col gap-0.5">
                  <Label htmlFor={`${id}-origin-${o}`}>{t(`upload.originLabel.${o}`)}</Label>
                  <p id={`${id}-origin-${o}-hint`} className="text-sm text-fg-secondary">
                    {t(`upload.originHint.${o}`)}
                  </p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </fieldset>
      )}
    </>
  );
}

const toggle = (set: ReadonlySet<string>, ref: string, on: boolean) => {
  const next = new Set(set);
  if (on) {
    next.add(ref);
  } else {
    next.delete(ref);
  }
  return next;
};

/** Checkbox lists of the tenant's groups and attribute rules, for the `groups` audience. */
function PrincipalPicker(props: {
  principals: PrincipalOptions;
  groups: ReadonlySet<string>;
  rules: ReadonlySet<string>;
  onGroups: (next: ReadonlySet<string>) => void;
  onRules: (next: ReadonlySet<string>) => void;
  missing: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const lists = [
    {
      key: "groups",
      label: t("upload.groupsLabel"),
      items: props.principals.groups.map((g) => ({ id: g.id, name: g.displayName })),
      picked: props.groups,
      onChange: props.onGroups,
    },
    {
      key: "rules",
      label: t("upload.rulesLabel"),
      items: props.principals.rules.map((r) => ({ id: r.id, name: r.name })),
      picked: props.rules,
      onChange: props.onRules,
    },
  ].filter((l) => l.items.length > 0);
  return (
    <div className="ml-6.5 flex flex-col gap-3 border-l border-line pl-3">
      <p id={`${id}-hint`} className="text-sm text-fg-secondary">
        {t("upload.groupsHint")}
      </p>
      {lists.map((list) => (
        <div key={list.key} className="flex flex-col gap-2">
          <p className="text-xs font-medium text-fg-muted">{list.label}</p>
          <ul className="flex max-h-48 flex-col gap-2 overflow-y-auto">
            {list.items.map((item) => (
              <li key={item.id} className="flex items-center gap-2.5">
                <Checkbox
                  id={`${id}-${list.key}-${item.id}`}
                  aria-describedby={`${id}-hint`}
                  checked={list.picked.has(item.id)}
                  onCheckedChange={(on) => list.onChange(toggle(list.picked, item.id, on === true))}
                />
                <Label htmlFor={`${id}-${list.key}-${item.id}`} className="font-normal">
                  {item.name}
                </Label>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {props.missing ? <p className="text-sm text-fg-secondary">{t("upload.pickOne")}</p> : null}
    </div>
  );
}
